import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ratesService } from "../../../services/ratesService";
import { rolesService } from "../../../services/rolesService";
import { categoriesService } from "../../../services/categoriesService";
import { AdminLayout } from "../../layout/AdminLayout";
import { toast } from "react-toastify";

export const RateForm = () => {
  const [roleId, setRoleId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [competency, setCompetency] = useState("Beginner");
  const [rate, setRate] = useState("");
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isNew, setIsNew] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    loadFormData();
  }, []);

  useEffect(() => {
    if (id && id !== "new") {
      setIsNew(false);
      loadRate(id);
    }
  }, [id]);

  const loadFormData = async () => {
    try {
      const [rolesData, categoriesData] = await Promise.all([
        rolesService.getAll(1, 1000),
        categoriesService.getAll(1, 1000),
      ]);
      setRoles(rolesData.data || []);
      setCategories(categoriesData.data || []);
    } catch (error) {
      console.log("Error loading form data:", error);
      toast.error("Failed to load form data");
    }
  };

  const loadRate = async (rateId) => {
    try {
      const data = await ratesService.getById(rateId);
      setRoleId(data.role_id?._id || "");
      setCategoryId(data.category_id?._id || "");
      setCompetency(data.competency || "Beginner");
      setRate(data.rate !== undefined ? String(data.rate) : "");
    } catch (error) {
      console.log("Error loading rate:", error);
      toast.error("Failed to load rate");
      navigate("/admin/rates");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!/^\d+(\.\d+)?$/.test(rate)) {
        toast.error("Rate must be a valid decimal number");
        setLoading(false);
        return;
      }

      const formData = {
        role_id: roleId,
        category_id: categoryId,
        competency,
        rate: Number(rate),
      };

      if (isNew) {
        await ratesService.create(formData);
        toast.success("Rate created");
      } else {
        await ratesService.update(id, formData);
        toast.success("Rate updated");
      }

      navigate("/admin/rates");
    } catch (error) {
      const errorMessage = error.response?.data?.message;
      if (Array.isArray(errorMessage)) {
        errorMessage.forEach((msg) => toast.error(msg));
      } else if (errorMessage) {
        toast.error(errorMessage);
      } else {
        toast.error("Failed to save rate");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRateChange = (value) => {
    // Allows only digits and a single dot, and dot cannot start the value.
    if (value === "") {
      setRate("");
      return;
    }

    if (!/^\d+(\.\d*)?$/.test(value)) {
      return;
    }

    setRate(value);
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">
            {isNew ? "Add Rate" : "Edit Rate"}
          </h1>

          <div className="bg-white rounded-lg shadow p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="roleId"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Role *
                </label>
                <select
                  id="roleId"
                  value={roleId}
                  onChange={(e) => setRoleId(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Role</option>
                  {roles.map((role) => (
                    <option key={role._id} value={role._id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="categoryId"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Category *
                </label>
                <select
                  id="categoryId"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Category</option>
                  {categories.map((category) => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="competency"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Competency Level *
                </label>
                <select
                  id="competency"
                  value={competency}
                  onChange={(e) => setCompetency(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Expert">Expert</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="rate"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Rate *
                </label>
                <div className="flex rounded-lg border border-gray-300 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent overflow-hidden">
                  <span className="px-4 py-2 bg-gray-100 text-gray-700 border-r border-gray-300">
                    $
                  </span>
                  <input
                    id="rate"
                    type="text"
                    value={rate}
                    onChange={(e) => handleRateChange(e.target.value)}
                    required
                    inputMode="decimal"
                    className="w-full px-4 py-2 outline-none"
                    placeholder="e.g., 25.50"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter decimal value. Dot is allowed once and between digits.
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg transition"
                >
                  {loading ? "Saving..." : "Save Rate"}
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/admin/rates")}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 rounded-lg transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};
